import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AI_ATTACHMENT_MAX_SIZE } from '@/constant/approval';
import useAiFileExtraction, {
  type UseAiFileExtractionOptions,
} from '@/hooks/useAiFileExtraction';
import { useSnackbar } from '@/hooks/global-tools';
import useFileUploadStatus from '@/hooks/useFileUploadStatus';
import AiFileAttachment, {
  type AiFileAttachmentProps,
} from './AiFileAttachment';
import type { FileUploadRejection } from './FileUpload';

export type AiFileUploaderProps<TResult> = UseAiFileExtractionOptions<TResult> &
  Omit<
    AiFileAttachmentProps,
    'analyzing' | 'onCancel' | 'onFilesAdded' | 'onError' | 'progress'
  >;

/**
 * AI 파일 분석 업로더 — AiFileAttachment(UI) + useAiFileExtraction(업로드·SSE 분석).
 * 양식별 차이는 요청/조회 경로·documentType·결과 반영(onResult)만 넘기면 됨
 */
const AiFileUploader = <TResult,>({
  requestUrl,
  getResultUrl,
  buildFiles,
  requestBody,
  uploadVariant,
  documentType,
  parseResult,
  onUploadStart,
  onUploadSettled,
  onUploaded,
  onResult,
  onProcessingChange,
  maxSize,
  ...uiProps
}: AiFileUploaderProps<TResult>) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { addError } = useFileUploadStatus();
  const { start, cancel, isProcessing, progress } = useAiFileExtraction({
    requestUrl,
    getResultUrl,
    buildFiles,
    requestBody,
    uploadVariant,
    documentType,
    parseResult,
    onUploadStart,
    onUploadSettled,
    onUploaded,
    onResult,
    onProcessingChange,
  });

  const handleRejected = useCallback(
    (rejections: FileUploadRejection[]) => {
      // 여러 개를 한 번에 넣으면 상태 모달이 아니라 알림 1회(레거시 msg.error)
      if (rejections.some((r) => r.reason === 'TOO_MANY_FILES')) {
        snackbar.showError({
          message: t('첨부파일은 한번에 1개만 등록할 수 있습니다.'),
        });
        return;
      }
      rejections.forEach(({ file, reason }) => {
        const message =
          reason === 'EMPTY_FILE'
            ? t('0MB 파일은 업로드할 수 없습니다.')
            : reason === 'OVER_MAX_SIZE'
            ? t(
                '파일 용량이 너무 큽니다. 최대 {{mb}}MB까지 업로드할 수 있습니다.',
                {
                  mb: Math.floor(
                    (maxSize ?? AI_ATTACHMENT_MAX_SIZE) / 1024 / 1024,
                  ),
                },
              )
            : reason === 'NAME_TOO_LONG'
            ? t('파일명이 {{max}}자를 초과합니다.', { max: 200 })
            : t('해당 파일 형식은 지원되지 않습니다.');
        addError(file.name, message);
      });
    },
    [addError, maxSize, snackbar, t],
  );

  return (
    <AiFileAttachment
      {...uiProps}
      maxSize={maxSize}
      analyzing={isProcessing}
      onCancel={cancel}
      progress={isProcessing ? progress : undefined}
      onFilesAdded={(files) => {
        if (files[0]) start(files[0]);
      }}
      onError={handleRejected}
    />
  );
};

export default AiFileUploader;
